import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { getFontFamily } from '@/theme/typography';
import { DEPTH_COLORS } from './treemap-constants';
import { hexToRgba } from './treemap-format';
import type { ParsedTreemap, TreemapTreeNode } from './treemap-types';
const COMPACT_PREVIEW_SECTION_LIMIT = 4;
const MIN_PREVIEW_WIDTH_PERCENT = 16;
const MIN_PREVIEW_CELL_WIDTH = 44;
const PREVIEW_CELL_HEIGHT = 26;
const COMPACT_PREVIEW_ROOT_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    height: '100%',
    justifyContent: 'center',
    paddingBottom: 28,
};
function getCompactPreviewSections(parsed: ParsedTreemap | null): TreemapTreeNode[] {
    if (!parsed) {
        return [];
    }
    return parsed.root.children
        .filter((child) => child.value > 0)
        .sort((first, second) => second.value - first.value)
        .slice(0, COMPACT_PREVIEW_SECTION_LIMIT);
}
function getPreviewTotal(sections: TreemapTreeNode[]): number {
    return sections.reduce((sum, section) => sum + section.value, 0) || 1;
}
function getPreviewSectionStyle(input: {
    section: TreemapTreeNode;
    index: number;
    previewTotal: number;
}): CSSProperties {
    const color = DEPTH_COLORS[input.index % DEPTH_COLORS.length];
    const widthPercent = Math.max(MIN_PREVIEW_WIDTH_PERCENT, Math.round((input.section.value / input.previewTotal) * 100));
    return {
        width: `${widthPercent}%`,
        minWidth: MIN_PREVIEW_CELL_WIDTH,
        height: PREVIEW_CELL_HEIGHT,
        borderRadius: 5,
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
        backgroundColor: hexToRgba(color, 0.18),
        border: `1px solid ${hexToRgba(color, 0.45)}`,
        fontFamily: getFontFamily(),
        fontSize: 10,
        color: 'rgba(255,255,255,0.88)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };
}
interface TreemapCompactPreviewProps {
    parsed: ParsedTreemap | null;
}
function TreemapCompactPreview({ parsed, }: TreemapCompactPreviewProps): ReactElement | null {
    const sections = getCompactPreviewSections(parsed);
    if (sections.length === 0) {
        return null;
    }
    const previewTotal = getPreviewTotal(sections);
    return (
    <div data-el="Cmarkdowntreemap/TreemapCompactPreview/Root" style={COMPACT_PREVIEW_ROOT_STYLE}>
      {sections.map((section, index) => (<div data-el="Cmarkdowntreemap/TreemapCompactPreview/Entry" key={section.id} style={getPreviewSectionStyle({ section, index, previewTotal })}>
          {section.label}
        </div>))}
    </div>
    );
}
export function getTreemapCompactPreview(parsed: ParsedTreemap | null): ReactNode {
    return <TreemapCompactPreview parsed={parsed}/>;
}
